import { Loading } from '@/components/ui/daisy';
import useLessonActivities from '@/data/hook/activity/useLessonActivities';

import InfinityList from '../ui/custom/InfinityList';
import ActivityBlockFormList from './ActivityBlockFormList';
import QuestionBankTrigger from './question-bank/QuestionBankTrigger';
import LessonActivityRow from './LessonActivityRow';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import { useCallback } from 'react';
import useNestedOptimisticReorder from '@/data/hook/useNestedOptimisticReorder';
import { adapterActivityQuestionOrder } from '@/utils/questions/adapterActivityQuestionOrder';
import Activity from '@/models/Activity';
import { CourseLineSubCategory } from '@/models/CourseLine';
import { Question, QuestionTypeEnum } from '@/models/Question';
import { DragDropList } from '../ui/custom/dataInput/DragDrop';

type QuestionsTabContentProps = {
  lessonId: number;
  subcategory?: CourseLineSubCategory;
  addBlockName?: string;
  allowedTypes?: QuestionTypeEnum[];
  createQuestionBtnName?: string;
  showBlockTypeSelector?: boolean;
};

export default function QuestionsTabContent({
  lessonId,
  subcategory,
  addBlockName,
  allowedTypes,
  createQuestionBtnName,
  showBlockTypeSelector = true,
}: QuestionsTabContentProps) {
  const {
    activities,
    isLoading,
    createActivity,
    isCreating,
    deleteActivity,
    updateActivity,
    isUpdating,
    cacheManager,
    reorderActivities,
    isReordering,
    ...infiniteProps
  } = useLessonActivities({ lessonId });

  const childrenMap = Object.fromEntries(
    activities.map(
      a => [String(a.id), a.questions ?? []] as [string, Question[]],
    ),
  );

  const handleReorder = useCallback(
    (
      reorderedParents: Activity[],
      reorderedChildrenMap: Record<string, Question[]>,
    ) => {
      const order = adapterActivityQuestionOrder(
        reorderedParents,
        reorderedChildrenMap,
      );

      reorderActivities(order);
    },
    [reorderActivities],
  );

  const {
    optimisticParents,
    optimisticChildrenMap,
    handleDragEnd,
    isDraggingParent,
    onBeforeCapture,
  } = useNestedOptimisticReorder({
    parents: activities,
    childrenMap,
    parentType: 'ACTIVITY',
    parentPrefix: 'activity',
    isReordering,
    onReorder: handleReorder,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ConditionalRenderer condition={optimisticParents.length > 0}>
        <InfinityList {...infiniteProps} className="relative mb-0 h-fit">
          <DragDropList
            onDragEnd={handleDragEnd}
            onBeforeCapture={onBeforeCapture}
            groupId="activities"
            type="ACTIVITY"
            customPlaceholder
            className="gap-0"
          >
            {optimisticParents.map((activity, index) => (
              <LessonActivityRow
                key={activity.id}
                isDraggable
                activity={{
                  ...activity,
                  questions: optimisticChildrenMap[activity.id] ?? [],
                }}
                index={index}
                lessonId={lessonId}
                cacheManager={cacheManager}
                onDelete={deleteActivity}
                onUpdate={updateActivity}
                isUpdating={isUpdating}
                isDragging={isDraggingParent}
                isReordering={isReordering}
                allowedTypes={allowedTypes}
                createQuestionBtnName={createQuestionBtnName}
              />
            ))}
          </DragDropList>
        </InfinityList>
      </ConditionalRenderer>
      <ActivityBlockFormList
        onSubmit={createActivity}
        isLoading={isCreating}
        addBlockName={addBlockName}
        showBlockTypeSelector={showBlockTypeSelector}
        actionsAddon={
          <QuestionBankTrigger context={{ lessonId, subcategory }} />
        }
      />
    </div>
  );
}
